import React from "react";
import { motion } from "framer-motion";
import {
  Building2,
  Sparkles,
  SprayCan,
  HardHat,
  Factory,
  Wind,
  Trash2,
  Droplets,
  ArrowUpRight,
} from "lucide-react";

const SERVICES = [
  {
    title: "Entretien de bureaux",
    desc: "Nettoyage complet quotidien ou périodique : sols, mobilier, sanitaires, cuisines, parties communes.",
    Icon: Building2,
    image:
      "https://images.unsplash.com/photo-1759156771079-6fef5b8d66c9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjbGVhbiUyMG9mZmljZSUyMGVtcHR5JTIwZGVza3xlbnwwfHx8fDE3Nzc5OTA3Mjl8MA&ixlib=rb-4.1.0&q=85",
    size: "col-span-12 md:col-span-8 row-span-2",
    tall: true,
  },
  {
    title: "Vitres & vitrines",
    desc: "Lavage intérieur/extérieur, façades accessibles, vitrines commerciales sans traces.",
    Icon: Wind,
    size: "col-span-12 md:col-span-4",
  },
  {
    title: "Désinfection",
    desc: "Protocole professionnel pour sanitaires, surfaces sensibles et environnements médicaux.",
    Icon: SprayCan,
    size: "col-span-12 md:col-span-4",
  },
  {
    title: "Traitement des sols",
    desc: "Aspiration, lavage mécanique, décapage, lustrage et cristallisation.",
    Icon: Sparkles,
    size: "col-span-6 md:col-span-4",
  },
  {
    title: "Après chantier",
    desc: "Remise en état complète après travaux, fin de chantier, sinistre ou débarras.",
    Icon: HardHat,
    size: "col-span-6 md:col-span-4",
    image:
      "https://images.unsplash.com/photo-1777001493167-c44f3a6d1deb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjbGVhbmVyJTIwY29tbWVyY2lhbHxlbnwwfHx8fDE3Nzc5OTA3Mjl8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Nettoyage industriel",
    desc: "Entrepôts, ateliers, sites techniques — matériel adapté aux environnements exigeants.",
    Icon: Factory,
    size: "col-span-12 md:col-span-4",
    image:
      "https://images.unsplash.com/photo-1759547021596-006943692ebe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxqYW5pdG9yJTIwZmxvb3IlMjBjbGVhbmluZ3xlbnwwfHx8fDE3Nzc5OTA3MzR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Haute pression & parkings",
    desc: "Nettoyage haute pression pour parkings, façades, cours et voiries privées.",
    Icon: Droplets,
    size: "col-span-6 md:col-span-4",
  },
  {
    title: "Consommables d'hygiène",
    desc: "Gestion et réapprovisionnement : papier, savon, essuie-mains, produits.",
    Icon: Trash2,
    size: "col-span-6 md:col-span-4",
  },
];

export default function Services() {
  return (
    <section id="services" className="relative py-24 md:py-32 bg-white" data-testid="services-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.3em] text-[#2E9E6E] font-medium mb-4">
              — Nos prestations
            </div>
            <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl text-[#0F2B5B] leading-[1.02] tracking-tight">
              Un savoir-faire complet pour chaque type d'espace professionnel.
            </h2>
          </div>
          <p className="text-slate-600 md:max-w-sm text-base leading-relaxed">
            Prestations régulières ou ponctuelles, toujours adaptées à votre
            activité, vos contraintes horaires et vos exigences qualité.
          </p>
        </div>

        <div className="grid grid-cols-12 auto-rows-[200px] gap-4 md:gap-5">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className={`group relative ${s.size} bg-[#F8FAFC] border border-slate-200 p-6 md:p-7 overflow-hidden hover:border-[#0F2B5B] transition-colors`}
              data-testid={`service-card-${i}`}
            >
              {s.image && (
                <>
                  <img
                    src={s.image}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F2B5B]/90 via-[#0F2B5B]/40 to-transparent" />
                </>
              )}
              <div className={`relative h-full flex flex-col justify-between ${s.image ? "text-white" : "text-[#0F172A]"}`}>
                <div className="flex items-start justify-between">
                  <s.Icon
                    className={`w-8 h-8 ${s.image ? "text-white" : "text-[#0F2B5B]"}`}
                    strokeWidth={1.5}
                  />
                  <ArrowUpRight
                    className={`w-5 h-5 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all ${s.image ? "text-white" : "text-[#2E9E6E]"}`}
                    strokeWidth={1.5}
                  />
                </div>
                <div>
                  <h3 className={`font-display text-xl md:text-2xl font-semibold leading-tight mb-2 ${s.image ? "text-white" : "text-[#0F2B5B]"}`}>
                    {s.title}
                  </h3>
                  <p className={`text-sm leading-relaxed ${s.image ? "text-white/85" : "text-slate-600"}`}>
                    {s.desc}
                  </p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
